/**
 * Cekirdek bilesenler.
 *
 * ── TEK DOSYADA OLMASININ SEBEBI ─────────────────────────────────────────────
 * Bunlar birbirinden bagimsiz degil, bir SISTEM. Button'in odak halkasi ile
 * Input'unki ayni olmali; Badge'in renkleri Button'in "danger" tonuyla
 * uyusmali. Ayri dosyalara bolmek, zamanla birbirinden ayrisan on farkli
 * "primary" tonu uretir.
 *
 * ── TASARIM KURALLARI ────────────────────────────────────────────────────────
 *  - Yalnizca semantic token. Hicbir bilesende ham hex YOK.
 *  - Her etkilesimli ogenin erisilebilir adi var (`aria-label` veya metin).
 *  - Yukleniyor durumunda buton DEVRE DISI ve bunu ekran okuyucuya bildirir.
 *  - Dokunma hedefi en az 44px — Apple/WCAG tabaninda parmakla vurulabilmeli.
 */

import {
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import './ui.css';

// ─── Button ──────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Ikon-yalniz butonlarda ZORUNLU — aksi halde ekran okuyucu "buton" der, hepsi bu. */
  iconOnly?: boolean;
  leading?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, iconOnly, leading, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      {...rest}
      // Yukleniyorken tiklanabilir kalmasi, cift gonderim demektir.
      disabled={rest.disabled || loading}
      aria-busy={loading || undefined}
      className={[
        'btn',
        `btn--${variant}`,
        `btn--${size}`,
        iconOnly ? 'btn--icon' : '',
        rest.className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {loading ? <span className="btn__spinner" aria-hidden="true" /> : leading}
      {children}
    </button>
  );
});

// ─── Alan sarmalayici ────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  /** Alan bazli hata — sunucudan gelen `fields` sozlugunden. */
  error?: string | undefined;
  /** Yardim metni. Hata varsa GIZLENIR: iki mesaj birden kafa karistirir. */
  hint?: string | undefined;
  required?: boolean;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

export function Field({ label, error, hint, required, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  const renderedControl = children({ id, describedBy, invalid: Boolean(error) });
  const control = isValidElement(renderedControl)
    ? cloneElement(renderedControl as ReactElement<Record<string, unknown>>, {
        // Alan sözleşmesi çağrı noktasında unutulsa bile erişilebilirlik bağı kopmaz.
        required: (renderedControl.props as Record<string, unknown>).required || required || undefined,
        'aria-required': (renderedControl.props as Record<string, unknown>)['aria-required'] || required || undefined,
        'aria-describedby': [
          (renderedControl.props as Record<string, unknown>)['aria-describedby'],
          describedBy,
        ].filter(Boolean).join(' ') || undefined,
        'aria-invalid': (renderedControl.props as Record<string, unknown>)['aria-invalid'] || Boolean(error) || undefined,
      })
    : renderedControl;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {control}

      {error ? (
        // `role="alert"` — hata olustugunda ekran okuyucu HEMEN duyurur.
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

// ─── Input / Textarea / Select ───────────────────────────────────────────────

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      {...rest}
      aria-invalid={invalid || undefined}
      className={['input', invalid ? 'input--invalid' : '', rest.className ?? '']
        .filter(Boolean)
        .join(' ')}
    />
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea(rest, ref) {
    return <textarea ref={ref} rows={4} {...rest} className={`input input--area ${rest.className ?? ''}`} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ children, ...rest }, ref) {
    return (
      <select ref={ref} {...rest} className={`input input--select ${rest.className ?? ''}`}>
        {children}
      </select>
    );
  },
);

// ─── Kart ────────────────────────────────────────────────────────────────────

/**
 * @param form Icerik bir FORM mu? Oyleyse alanlar okunabilir olcude
 *   (`--measure-form`) tutulur. Bu olmadan tek satirlik bir "Ad soyad" alani
 *   1180 piksele yayiliyordu — gozun alanin basi ile sonu arasinda gidip
 *   gelmesi demek.
 */
export function Card({
  title,
  action,
  children,
  padded = true,
  form = false,
  className,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  padded?: boolean;
  form?: boolean;
  className?: string;
}) {
  const bodyClass = padded ? (form ? 'card__body card__body--form' : 'card__body') : '';

  return (
    <section className={className ? `card ${className}` : 'card'}>
      {(title || action) && (
        <header className="card__head">
          {typeof title === 'string' ? <h2 className="card__title">{title}</h2> : title}
          {action}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

// ─── Rozet ───────────────────────────────────────────────────────────────────

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'primary';

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

/**
 * Durum noktasi + metin.
 *
 * RENK TEK BASINA ANLAM TASIMAZ: renk korlugu olan kullanici yesil/kirmizi
 * ayrimini goremez. Bu yuzden nokta ile birlikte HER ZAMAN metin var.
 */
export function StatusDot({ tone, label }: { tone: Tone; label: string }) {
  return (
    <span className="status">
      <span className={`status__dot status__dot--${tone}`} aria-hidden="true" />
      {label}
    </span>
  );
}

// ─── Durum ekranlari ─────────────────────────────────────────────────────────

/**
 * Bos durum.
 *
 * "Kayit yok" yazmak yetmez. Kullanici NEDEN bos oldugunu ve NE YAPACAGINI
 * bilmeli — bu yuzden `action` neredeyse her zaman dolu olmali.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon && <div className="empty__icon" aria-hidden="true">{icon}</div>}
      <h3 className="empty__title">{title}</h3>
      <p className="empty__text">{description}</p>
      {action && <div className="empty__action">{action}</div>}
    </div>
  );
}

/**
 * Hata durumu.
 *
 * Iki sey SOYLEMEK ZORUNDA: ne oldu, ne yapilacak. "Bir hata oluştu" tek
 * basina kullaniciyi cikmaza sokar; bu yuzden `onRetry` neredeyse her zaman
 * verilir.
 */
export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const isEnglish = typeof document !== 'undefined' && document.documentElement.lang.startsWith('en');
  return (
    <div className="empty" role="alert">
      <div className="empty__icon empty__icon--danger" aria-hidden="true">
        !
      </div>
      <h3 className="empty__title">{isEnglish ? 'Something went wrong' : 'Bir sorun çıktı'}</h3>
      <p className="empty__text">{message}</p>
      {onRetry && (
        <div className="empty__action">
          <Button variant="secondary" onClick={onRetry}>
            {isEnglish ? 'Try again' : 'Tekrar dene'}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Iskelet yukleme.
 *
 * Donen bir carkifelek yerine ICERIGIN SEKLINI gosteriyoruz: kullanici ne
 * gelecegini onceden gorur, yukleme daha kisa hissedilir ve icerik gelince
 * sayfa ziplamaz (layout shift).
 */
export function Skeleton({ height = 20, width = '100%', radius = 'var(--radius-sm)' }: {
  height?: number | string;
  width?: number | string;
  radius?: string;
}) {
  return (
    <span
      className="skeleton"
      style={{ height, width, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

export function LoadingBlock({ rows = 3, label = 'Yükleniyor' }: { rows?: number; label?: string }) {
  return (
    <div className="loading" aria-live="polite" aria-busy="true">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={i === 0 ? 28 : 18} width={i === 0 ? '40%' : '100%'} />
      ))}
    </div>
  );
}

// ─── Modal ───────────────────────────────────────────────────────────────────

/**
 * Modal.
 *
 * Yerli `<dialog>` kullaniliyor: odak tuzagi, Esc ile kapanma ve arka planin
 * etkisizlestirilmesi tarayici tarafindan DOGRU yapiliyor. Elle yazilan modal
 * kodlarinin cogu odak yonetimini kacirir ve klavye kullanicisini arka planda
 * kaybeder.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  initialFocus = 'body',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  initialFocus?: 'body' | 'footer' | 'close';
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const closeLabel = typeof document !== 'undefined' && document.documentElement.lang.startsWith('en') ? 'Close' : 'Kapat';

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const previousFocus=document.activeElement instanceof HTMLElement?document.activeElement:null;
    let focusFrame = 0;
    if (open && !dialog.open) {
      dialog.showModal();
      focusFrame = window.requestAnimationFrame(() => {
        const selector = initialFocus === 'close'
          ? '.modal__close'
          : initialFocus === 'footer'
            ? '.modal__foot button:not(:disabled)'
            : '.modal__body input:not(:disabled), .modal__body select:not(:disabled), .modal__body textarea:not(:disabled), .modal__body button:not(:disabled), .modal__foot button:not(:disabled), .modal__close';
        dialog.querySelector<HTMLElement>(selector)?.focus();
      });
    }
    if (!open && dialog.open) dialog.close();
    return () => {window.cancelAnimationFrame(focusFrame);if(open){if(dialog.open)dialog.close();if(previousFocus?.isConnected)previousFocus.focus();}};
  }, [initialFocus, open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    // Gecikmiş close olayı yeniden açılmış kirli bir modalı kapatmamalı.
    const handler = () => { if(open&&!dialog.open)onClose(); };
    dialog.addEventListener('close', handler);
    return () => dialog.removeEventListener('close', handler);
  }, [onClose,open]);

  return (
    <dialog
      ref={ref}
      className={`modal modal--${size}`}
      aria-modal="true"
      onCancel={(event)=>{event.preventDefault();onClose();}}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <span className="modal__accent" aria-hidden="true" />
      <header className="modal__head">
        <div className="modal__heading">
          <h2 id={titleId} className="modal__title">
            {title}
          </h2>
          {description ? <p id={descriptionId} className="modal__description">{description}</p> : null}
        </div>
        <Button className="modal__close" variant="ghost" size="sm" iconOnly onClick={onClose} aria-label={closeLabel}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </Button>
      </header>
      {children ? <div className="modal__body">{children}</div> : null}
      {footer && <footer className="modal__foot">{footer}</footer>}
    </dialog>
  );
}

// ─── Drawer ──────────────────────────────────────────────────────────────────

/**
 * Kisa, baglamsal yonetim isleri icin sag panel.
 *
 * `dialog.showModal()` arka plani etkisizlestirir, odagi panel icinde tutar,
 * Escape'i yonetir ve kapaninca odagi tetikleyen kontrole geri verir. Uzun
 * editorler drawer kullanmaz; onlar ayri route olarak bestelenir.
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: 'md' | 'wide';
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const closeLabel = typeof document !== 'undefined' && document.documentElement.lang.startsWith('en') ? 'Close' : 'Kapat';

  useEffect(() => {
    const drawer = ref.current;
    if (!drawer) return;
    let focusFrame = 0;
    if (open && !drawer.open) {
      drawer.showModal();
      focusFrame = window.requestAnimationFrame(() => {
        drawer.querySelector<HTMLElement>('.drawer__close')?.focus();
      });
    }
    if (!open && drawer.open) drawer.close();
    return () => window.cancelAnimationFrame(focusFrame);
  }, [open]);

  useEffect(() => {
    const drawer = ref.current;
    if (!drawer) return;
    const handler = () => onClose();
    drawer.addEventListener('close', handler);
    return () => drawer.removeEventListener('close', handler);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      className={`drawer drawer--${size}`}
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <header className="drawer__head">
        <div>
          <h2 id={titleId} className="drawer__title">{title}</h2>
          {description ? <p id={descriptionId} className="drawer__description">{description}</p> : null}
        </div>
        <Button className="drawer__close" variant="ghost" size="sm" iconOnly onClick={onClose} aria-label={closeLabel}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </Button>
      </header>
      <div className="drawer__body">{children}</div>
    </dialog>
  );
}

/**
 * Geri alinmasi zor islemlerde tarayicinin markasiz `confirm()` penceresi
 * yerine ayni odak, tema ve mobil davranisini kullanan karar diyaloğu.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Onayla',
  cancelLabel = 'Vazgeç',
  danger = false,
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="sm"
      initialFocus="footer"
      footer={
        <>
          <Button onClick={onClose}>{cancelLabel}</Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  );
}

// ─── Tablo ───────────────────────────────────────────────────────────────────

/**
 * Tablo sarmalayici.
 *
 * Dar ekranda tablo yatay kayar; SAYFA kaymaz. Sayfanin yatay kaymasi mobilde
 * en sik gorulen duzen hatasidir.
 */
export function TableWrap({ children, caption }: { children: ReactNode; caption?: string }) {
  return (
    <div className="table-wrap" tabIndex={0} role="region" aria-label={caption ?? 'Tablo'}>
      <table className="table">{children}</table>
    </div>
  );
}

// ─── Sekmeler ────────────────────────────────────────────────────────────────

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: Array<{ id: T; label: string; count?: number }>;
  value: T;
  onChange: (id: T) => void;
}) {
  const baseId = useId();
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const moveFocus = (index: number) => {
    const next = (index + tabs.length) % tabs.length;
    const tab = tabs[next];
    if (!tab) return;
    onChange(tab.id);
    buttonRefs.current[next]?.focus();
  };

  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab, index) => (
        <button
          key={tab.id}
          ref={(element) => { buttonRefs.current[index] = element; }}
          id={`${baseId}-tab-${tab.id}`}
          type="button"
          role="tab"
          aria-selected={tab.id === value}
          tabIndex={tab.id === value ? 0 : -1}
          className={`tabs__tab ${tab.id === value ? 'is-active' : ''}`}
          onClick={() => onChange(tab.id)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') { event.preventDefault(); moveFocus(index + 1); }
            if (event.key === 'ArrowLeft') { event.preventDefault(); moveFocus(index - 1); }
            if (event.key === 'Home') { event.preventDefault(); moveFocus(0); }
            if (event.key === 'End') { event.preventDefault(); moveFocus(tabs.length - 1); }
          }}
        >
          {tab.label}
          {tab.count !== undefined && <span className="tabs__count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ─── Sayfa basligi ───────────────────────────────────────────────────────────

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      <div>
        <h1 className="page-head__title">{title}</h1>
        {description && <p className="page-head__desc">{description}</p>}
      </div>
      {actions && <div className="page-head__actions">{actions}</div>}
    </header>
  );
}

// ─── Onay kutusu ─────────────────────────────────────────────────────────────

export function Checkbox({
  label,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className="checkbox">
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}
